/**
 * useActionsBadgeCount — track:attention-dot-one-count (spec #49392).
 *
 * The one number behind the sidebar dot: the same derivation the Actions
 * tab-bar badge uses (lib/actionsCount), fed by the same standalone hooks, so
 * the dot is lit exactly when the Actions tab has something. Lives outside the
 * Control Center, so it cannot use useRelayData (no provider up here) and must
 * not import the tab itself (that would defeat the lazy load).
 */
import { useEffect, useMemo, useState } from "react";
import { useActiveSprint, useModalQueue, useTaggedTeamLog } from "../lib/RelayDataProvider";
import { ACTIONS_TAB_TAGS, computeActionsBadge, humanRosterAuthorsFromMembers } from "../lib/actionsCount";
import { getCachedAttentionCount, setCachedAttentionCount } from "../lib/appShellCache";
import { useWorkspaceMembers } from "./useWorkspaceMembers";

export function useActionsBadgeCount(workspaceId: string): { count: number; ready: boolean } {
  const { messages, loading: teamLogLoading } = useTaggedTeamLog(workspaceId, ACTIONS_TAB_TAGS);
  const { items: modalItems } = useModalQueue(workspaceId);
  const activeSprint = useActiveSprint(workspaceId);
  const { members, loading: membersLoading, error: membersError } = useWorkspaceMembers(workspaceId);
  const [cachedCount, setCachedCount] = useState<number | null>(null);

  useEffect(() => {
    if (!workspaceId) {
      setCachedCount(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const cached = await getCachedAttentionCount(workspaceId);
        if (!cancelled) setCachedCount(cached?.count ?? null);
      } catch {
        if (!cancelled) setCachedCount(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const ready = !!workspaceId && !teamLogLoading && !membersLoading;
  // Roster only once the fetch finished cleanly; otherwise the coarser
  // roster-less derivation (any requeue counts) is the tab's own fallback.
  const roster = useMemo(
    () => (ready && !membersError ? humanRosterAuthorsFromMembers(members) : undefined),
    [ready, membersError, members],
  );
  const liveCount = useMemo(
    () => computeActionsBadge(messages, activeSprint, modalItems, roster),
    [messages, activeSprint, modalItems, roster],
  );

  useEffect(() => {
    if (!ready) return;
    (async () => {
      try {
        await setCachedAttentionCount(workspaceId, { count: liveCount, tier1Count: liveCount });
      } catch {
        // cache is best-effort
      }
    })();
  }, [ready, workspaceId, liveCount]);

  if (!workspaceId) return { count: 0, ready: false };
  return { count: ready ? liveCount : cachedCount ?? 0, ready };
}
